import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import {
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  ToggleButton,
  IconButton,
  ToggleButtonGroup,
  Divider,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import BtcIcon from "../../assets/btc.svg";
import UsdtIcon from "../../assets/usdt.svg";
import EthIcon from "../../assets/eth.svg";
import UsdcIcon from "../../assets/usdc.svg";
import MovingIcon from "@mui/icons-material/Moving";
import { refreshToken } from "../../utils/Token";

const generateMockTransactions = (num) => {
  const transactions = [];
  for (let i = 0; i < num; i++) {
    transactions.push({
      id: i,
      blockHash: `0x${Math.random().toString(16).substr(2, 64)}`,
      blockNumber: Math.floor(Math.random() * 1000000),
      from: `0x${Math.random().toString(16).substr(2, 40)}`,
      to: `0x${Math.random().toString(16).substr(2, 40)}`,
      value: (Math.random() * 10).toFixed(4),
      gas: Math.floor(Math.random() * 100000),
      gasPrice: (Math.random() * 100).toFixed(2),
      amount: (Math.random() * 1000).toFixed(2),
      currency: ["ETH", "BTC", "USDT", "USDC"][Math.floor(Math.random() * 4)],
      timestamp: new Date().toISOString(),
    });
  }
  return transactions;
};

const Wallets = () => {
  const VISIBLE_FIELDS = [
    "blockHash",
    "blockNumber",
    "from",
    "to",
    "value",
    "gas",
    "gasPrice",
    "amount",
    "currency",
    "timestamp",
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const cryptoAccounts = [
    {
      currency: "BTC",
      currencyName: "Bitcoin",
      symbol: "₿",
      balance: "0.5",
      onHold: "0.1",
      icon: BtcIcon,
      address: "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa",
    },
    {
      currency: "USDT",
      network: "ERC20",
      currencyName: "Tether",
      symbol: "₮",
      balance: "800",
      onHold: "50",
      icon: UsdtIcon,
      address: "0x1234abcd...ERC",
    },
    {
      currency: "USDT",
      network: "TRC20",
      currencyName: "Tether",
      symbol: "₮",
      balance: "500",
      onHold: "30",
      icon: UsdtIcon,
      address: "T9yD14Nj...TRC",
    },
    {
      currency: "USDT",
      network: "BSC",
      currencyName: "Tether",
      symbol: "₮",
      balance: "600",
      onHold: "20",
      icon: UsdtIcon,
      address: "0x4567bsc...BSC",
    },
    {
        currency: "USDT",
        network: "Polygon",
        currencyName: "Tether",
        symbol: "₮",
        balance: "600",
        onHold: "20",
        icon: UsdtIcon,
        address: "0x4567bsc...BSC",
      },
    {
      currency: "USDC",
      network: "TRC20",
      currencyName: "USD Coin",
      symbol: "₮",
      balance: "400",
      onHold: "10",
      icon: UsdcIcon,
      address: "SolanaAddress...USDC",
    },
    {
      currency: "USDC",
      network: "ERC20",
      currencyName: "USD Coin",
      symbol: "₮",
      balance: "700",
      onHold: "40",
      icon: UsdcIcon,
      address: "ERC20Address...USDC",
    },
    {
        currency: "USDC",
        network: "BSC",
        currencyName: "USD Coin",
        symbol: "₮",
        balance: "400",
        onHold: "10",
        icon: UsdcIcon,
        address: "SolanaAddress...USDC",
      },
      {
        currency: "USDC",
        network: "Polygon",
        currencyName: "USD Coin",
        symbol: "₮",
        balance: "400",
        onHold: "10",
        icon: UsdcIcon,
        address: "SolanaAddress...USDC",
      },
    {
      currency: "ETH",
      currencyName: "Ethereum",
      symbol: "Ξ",
      balance: "10",
      onHold: "1",
      icon: EthIcon,
      address: "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = Cookies.get("token");
        const response = await axios.get("/api/dashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage(response.data);
      } catch (error) {
        setMessage("Error fetching dashboard data");
        navigate("/login");
      }
    };

    fetchData();
    const interval = setInterval(() => {
      refreshToken();
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    const transactions = generateMockTransactions(100);
    setData(transactions);
    setLoading(false);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const initialCurrency = queryParams.get("tab") || "BTC";
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [showBalances, setShowBalances] = useState(false);

  const currencyAccounts = cryptoAccounts.filter(
    (acc) => acc.currency === selectedCurrency
  );
  const hasMultipleNetworks = currencyAccounts.length > 1;

  const selectedAccount =
    currencyAccounts.find((acc) => acc.network === selectedNetwork) ||
    currencyAccounts[0];

  useEffect(() => {
    const tab = queryParams.get("tab");
    if (tab && tab !== selectedCurrency) {
      setSelectedCurrency(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (currencyAccounts.length > 0) {
      setSelectedNetwork(currencyAccounts[0].network || null);
    }
  }, [selectedCurrency]);

  const handleCurrencyChange = (event, newCurrency) => {
    if (newCurrency !== null) {
      setSelectedCurrency(newCurrency);
      navigate(`?tab=${newCurrency}`, { replace: true });
    }
  };

  return (
    <Container maxWidth="100" sx={{ backgroundColor: "#f5f5f5" }}>

      <Card sx={{ borderRadius: 4, boxShadow: 0, mt: 2 }}>
        <CardContent>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Recent Transactions
          </Typography>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={data.slice(-4)}
              columns={VISIBLE_FIELDS.map((field) => ({
                field,
                headerName: field,
                width: 150,
              }))}
              loading={loading}
              components={{ Toolbar: GridToolbar }}
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Wallets;
